"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { usePathname } from "next/navigation";
import { Icon } from "@iconify/react";

type Messages = {
  [key: string]: {
    title: string;
    description: string;
    button: string;
    contact: {
      text: string;
      email: string;
    };
  };
};

export default function NotFound() {
  const pathname = usePathname();
  const locale = pathname.startsWith("/en") ? "en" : "fr";

  const messages: Messages = {
    fr: {
      title: "Page non trouvée",
      description:
        "La page que vous recherchez n'existe pas ou a été déplacée.",
      button: "Retour à l'accueil",
      contact: {
        text: "Si vous pensez qu'il s'agit d'une erreur, contactez-nous à",
        email: "contact@virtuosa.fr",
      },
    },
    en: {
      title: "Page not found",
      description:
        "The page you are looking for does not exist or has been moved.",
      button: "Back to home",
      contact: {
        text: "If you think this is an error, contact us at",
        email: "contact@virtuosa.fr",
      },
    },
  };

  const t = (key: string): string => {
    const keys = key.split(".");
    let value: any = messages[locale];
    for (const k of keys) {
      value = value[k];
    }
    return value;
  };

  // Variants pour les animations cohérentes
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <section className="flex min-h-screen flex-col items-center justify-center px-4 py-20 ph:px-24 md:px-36 lg:px-48 xl:px-60">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex max-w-2xl flex-col items-center text-center"
      >
        {/* 404 */}
        <motion.div variants={itemVariants} className="mb-8">
          <h1 className="text-8xl font-medium text-secondary sm:text-9xl md:text-[12rem]">
            404
          </h1>
        </motion.div>

        {/* Titre principal */}
        <motion.h2
          variants={itemVariants}
          className="mb-6 font-sentient text-2xl font-medium text-secondary sm:text-3xl md:text-4xl lg:text-5xl"
        >
          {t("title")}
        </motion.h2>

        {/* Description */}
        <motion.p
          variants={itemVariants}
          className="mb-8 text-lg text-accent lg:text-xl"
        >
          {t("description")}
        </motion.p>

        {/* Boutons d'action */}
        <motion.div
          variants={containerVariants}
          className="flex flex-col gap-4 sm:flex-row sm:gap-6"
        >
          <motion.div variants={buttonVariants}>
            <Link
              href="/"
              className="group relative inline-block overflow-hidden rounded-lg bg-secondary p-[12px] text-primary transition-all duration-300 ease-in-out"
            >
              <Icon
                icon="mdi:play"
                className="absolute left-3 top-1/2 -translate-x-2 -translate-y-1/2 transform opacity-0 transition-all duration-300 ease-in-out group-hover:translate-x-0 group-hover:opacity-100"
              />
              <span className="transition-all duration-300 ease-in-out group-hover:ml-6">
                {t("button")}
              </span>
            </Link>
          </motion.div>
        </motion.div>

        {/* Contact alternatif */}
        <motion.div variants={itemVariants} className="mt-12">
          <p className="text-accent">
            {t("contact.text")}{" "}
            <a
              href={`mailto:${t("contact.email")}`}
              className="text-secondary hover:underline"
            >
              {t("contact.email")}
            </a>
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
}
